/// <reference types="jquery.validation"/>

import type {FrontendAppsettingsModel} from "../../Scalesoft.HealthPlatform.TelemedicineV2.Core/Scripts/AjaxWebHubClients.generated";
import {CookieNames, CustomHeaderNames} from "./Generated/ConstantsWebHub.generated";

declare const pageLoadTime: number;
declare const timeCorrection: number;
declare const frontendConfig: FrontendAppsettingsModel;

export default class Utils {
    private readonly disableLoginProlongHttpHeader = CustomHeaderNames.DisableLoginProlong
    private static loadedConfiguration: FrontendAppsettingsModel;
    private static baseUrl: string | null = null;
    private static antiForgeryToken: string | null = null;

    public static initUtils(antiforgeryTokenValue: string | null = null) {
        
        /// AntiForgery token was generated for SPA as part of initial request cookies, then loaded 
        /// https://learn.microsoft.com/en-us/aspnet/core/security/anti-request-forgery?view=aspnetcore-9.0#javascript-ajax-and-spas
        this.antiForgeryToken =antiforgeryTokenValue;
        
        Utils.baseUrl = document.getElementById("base-url")?.getAttribute("data-path") ?? null;
        if (Utils.baseUrl === null) {
            throw new Error("Base URL element not found");
        }
    }

    public resolveReferences(json: string): any {
        let jsonObject = JSON.parse(json);

        const byid = {} as any; // all objects by id
        const refs: any[] = []; // references to objects that could not be resolved
        jsonObject = (function recurse(obj, prop?: any, parent?: any): any {
            if (typeof obj !== "object" || !obj) { // a primitive value
                return obj;
            }
            if (Object.prototype.toString.call(obj) === "[object Array]") {
                for (let i: number = 0; i < obj.length; i++) {
                    // check also if the array element is not a primitive value
                    if (typeof obj[i] !== "object" || !obj[i]) { // a primitive value
                        continue;
                    } else if ("$ref" in obj[i]) {
                        obj[i] = recurse(obj[i], i, obj);
                    } else {
                        obj[i] = recurse(obj[i], prop, obj);
                    }
                }
                return obj;
            }
            if ("$ref" in obj) { // a reference
                const ref = obj.$ref;
                if (ref in byid) {
                    return byid[ref];
                }
                // else we have to make it lazy:
                refs.push([parent, prop, ref]);
                return;
            } else if ("$id" in obj) {
                const id = obj.$id;
                delete obj.$id;
                if ("$values" in obj) { // an array
                    obj = obj.$values.map(recurse);
                } else { // a plain object
                    for (const plainObjProp in obj) {
                        if (obj.hasOwnProperty(plainObjProp)) {
                            obj[plainObjProp] = recurse(obj[plainObjProp], plainObjProp, obj);
                        }
                    }
                }
                byid[id] = obj;
            }
            return obj;
        })(jsonObject); // run it!

        for (const ref of refs) { // resolve previously unknown references
            ref[0][ref[1]] = byid[ref[2]];
            // Notice that this throws if you put in a reference at top-level
        }
        return jsonObject;
    }

    /* RFC4122 version 4 compliant UUID generation
       Taken from https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
    */

    /* tslint:disable:no-bitwise */
    public generateUUID(): string {
        let d = new Date().getTime();
        if (typeof performance !== "undefined" && typeof performance.now === "function") {
            d += performance.now(); // use high-precision timer if available
        }
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g,
            (char) => {
                const r = (d + Math.random() * 16) % 16 | 0;
                d = Math.floor(d / 16);
                return (char === "x" ? r : (r & 0x3 | 0x8)).toString(16);
            });
    }

    /* tslint:enable:no-bitwise */

    public searchArrayByPropertyValue<T>(array: T[], prop: keyof T, key: T[keyof T]): T | null {
        let i: 0;
        for (i = 0; i < array.length; i++) {
            if (array[i][prop] === key) {
                return array[i];
            }
        }
        return null;
    }

    // Returns a function, that, as long as it continues to be invoked, will not
    // be triggered. The function will be called after it stops being called for
    // N milliseconds. If `immediate` is passed, trigger the function on the
    // leading edge, instead of the trailing.
    public debounce<T>(func: (...args: T[]) => any | void, wait: number, immediate: boolean): (...args: T[]) => void {
        let timeout: number | null = null;
        return function(...args: T[]): void {
            // @ts-ignore
            const context = this;
            const later = () => {
                timeout = null;
                if (!immediate) {
                    func.apply(context, args);
                }
            };
            const callNow = immediate && !timeout;
            if (timeout !== null) {
                window.clearTimeout(timeout);
            }
            timeout = window.setTimeout(later, wait);
            if (callNow) {
                func.apply(context, args);
            }
        };
    }

    public getPageLoadTime(): number {
        return pageLoadTime;
    }

    public getServerTimeCorrection(): number {
        return timeCorrection;
    }

    public static getBaseUrl(): string {
        if (Utils.baseUrl === null) {
            Utils.baseUrl = document.getElementById("base-url")?.getAttribute("data-path") ?? null;
        }

        if (Utils.baseUrl === null) {
            throw new Error("Base URL element not found");
        }
        return Utils.baseUrl;
    }

    public static isTestingEnvironment(): boolean {
        const stringValue = document.getElementById("isDevelopmentEnvEl")?.getAttribute("data-val");
        if (stringValue !== undefined && stringValue !== null) {
            return stringValue.toLowerCase() === "true";
        }

        throw new Error("Is testing environment element not found");
    }

    public getBaseUrl(): string {
        return Utils.getBaseUrl();
    }

    public static getAntiForgeryToken(): string | null {
        if (this.antiForgeryToken !== null) {
            return this.antiForgeryToken;
        }
        const antiForgeryTokenEl = document.getElementById("requestVerificationToken");
        const antiForgeryTokenValue = antiForgeryTokenEl?.getAttribute("value");
        return antiForgeryTokenValue ?? null;
    }

    public getLayout(): string | null {
        const layoutValueInput = document.getElementById("layoutValue") as HTMLInputElement;
        return layoutValueInput ? layoutValueInput.value : null;
    }

    public configureJqueryAjaxForInstance(jqueryInstance: JQueryStatic): void {
        jqueryInstance(document).ajaxSend((_, request, settings) => {
            this.addAntiforgeryTokenHeader(request, settings);
            this.addLayoutHeader(request, settings);
        });
        jqueryInstance.ajaxSetup({cache: false}); // disable IE AJAX caching by adding a timestamp request parameter
    }

    public configureJqueryValidator(jqInstance: JQueryStatic, localization: Localization): void {
        if (jqInstance?.validator === undefined) {
            return;
        }
        localization.translateAsync("field-is-required-message").then((value) => {
            jqInstance.extend(jqInstance.validator.messages, {
                required: value.result.value,
            });
        });
    }

    public updateAllLinks(jQueryInstance: JQueryStatic): void {
        const layoutValue = this.getLayout();

        if (!layoutValue) { // Skip if no layout specified
            return;
        }

        const layoutKey = "layout";

        jQueryInstance("a").attr("href",
            (_: number, uri: string) => {

                if (this.isUrlLocal(uri)) { // Modify only local requests
                    return this.updateQueryString(uri, layoutKey, layoutValue);
                }

                return uri;
            });

        jQueryInstance("form").attr("action",
            (_: number, uri: string) => {

                if (this.isUrlLocal(uri)) { // Modify only local requests
                    return this.updateQueryString(uri, layoutKey, layoutValue);
                }

                return uri;
            });
    }

    /**
     * Add or update a query string parameter. If no URI is given, we use the current
     * window.location.href value for the URI.
     *
     * Based on the DOM URL parser described here:
     * http://james.padolsey.com/javascript/parsing-urls-with-the-dom/
     * and here:
     * https://stackoverflow.com/questions/5999118/how-can-i-add-or-update-a-query-string-parameter/23529943#23529943
     *
     * @param   uri     (string)    Optional: The URI to add or update a parameter in
     * @param   key     (string)    The key to add or update
     * @param   value   (string)    The new value to set for key
     */
    public updateQueryString(uri: string, key: string, value: string): string {

        // Use window URL if no query string is provided
        if (!uri) {
            uri = window.location.href;
        }

        // Create a dummy element to parse the URI with
        const a = document.createElement("a");

        // match the key, optional square brackets, an equals sign or end of string, the optional value
        const regEx = new RegExp(key + "((?:\\[[^\\]]*\\])?)(=|$)(.*)");

        // Setup some additional variables
        let qs: string[];
        let qsLen: number;
        let keyFound = false;

        // Use the JS API to parse the URI
        a.href = uri;

        // If the URI doesn't have a query string, add it and return
        if (!a.search) {

            a.search = "?" + key + "=" + value;

            return a.href;
        }

        // Split the query string by ampersands
        qs = a.search.replace(/^\?/, "").split(/&(?:amp;)?/);
        qsLen = qs.length;

        // Loop through each query string part
        while (qsLen > 0) {

            qsLen--;

            // Remove empty elements to prevent double ampersands
            if (!qs[qsLen]) {
                qs.splice(qsLen, 1);
                continue;
            }

            // Check if the current part matches our key
            if (regEx.test(qs[qsLen])) {

                // Replace the current value
                qs[qsLen] = qs[qsLen].replace(regEx, key + "$1") + "=" + value;

                keyFound = true;
            }
        }

        // If we haven't replaced any occurrences above, add the new parameter and value
        if (!keyFound) {
            qs.push(key + "=" + value);
        }

        // Set the new query string
        a.search = "?" + qs.join("&");

        return a.href;
    }

    public getQueryParams(): { [name: string]: string; } {
        const result: { [name: string]: string; } = {};
        let hash: string[];

        let query = window.location.search;
        query = query.substring(1, query.length);

        const hashes = query.split("&");
        for (const queryHash of hashes) {
            hash = queryHash.split("=");
            result[hash[0]] = decodeURIComponent(hash[1]);
        }
        return result;
    }

    private addAntiforgeryTokenHeader(request: JQuery.jqXHR, settings: JQuery.AjaxSettings): void {
        if (this.isRequestLocal(settings)) { // Modify only local requests
            const token = Utils.getAntiForgeryToken();
            if(token === null) {
                throw new Error("Anti-forgery token element not found");
            }
            request.setRequestHeader("RequestVerificationToken", token);
        }
    }

    private addLayoutHeader(request: JQuery.jqXHR, settings: JQuery.AjaxSettings): void {
        const layout = this.getLayout();
        if (this.isRequestLocal(settings) && layout !== null) { // Modify only local requests
            request.setRequestHeader("Layout", layout);
        }
    }

    public isRequestLocal(settings: JQuery.AjaxSettings): boolean {
        return this.isUrlLocal(settings.url);
    }

    public isUrlLocal(url?: string): boolean {
        if (!url) {
            return false;
        }

        const baseUrl = this.getBaseUrl();
        return url.lastIndexOf(baseUrl, 0) === 0 ||
            url.lastIndexOf(window.location.origin, 0) === 0; // some AJAX requests are generated from window.location.href
    }

    public setParentOnlyText(parent: JQuery, text: string): void {
        const menuTextNodes = parent.contents().filter((_, element) => {
            return element.nodeType === Node.TEXT_NODE;
        });

        menuTextNodes.first().replaceWith(text);
    }

    public generateUniqueId(): string {
        let id: string;

        do {
            id = this.generateId(5);
        } while (document.getElementById(id) !== null);

        return id;
    }

    public generateId(length: number): string {
        let result = "";
        const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
        const charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    public getCurrentLanguage(localization: Localization): string {
        const culture = localization.getCurrentCulture();

        return this.getLanguageWithoutRegion(culture);
    }

    private getLanguageWithoutRegion(culture: string): string {
        const split = culture.split("-");

        return split[0];
    }

    public static escapeHtmlChars(unsafeString: string): string {
        if (unsafeString === null || unsafeString === undefined) {
            return "";
        }
        return unsafeString
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    public escapeHtmlChars(unsafeString: string): string {
        return Utils.escapeHtmlChars(unsafeString);
    }

    public static htmlDecode(val: string): string {
        const divElement = document.createElement("div");
        divElement.innerHTML = val;
        return divElement.textContent || divElement.innerText || "";
    }

    public getDisableLoginProlongHttpHeaderName(): string {
        return this.disableLoginProlongHttpHeader;
    }

    public static getConfiguration(): FrontendAppsettingsModel {
        if (!Utils.loadedConfiguration) {
            Utils.loadedConfiguration = frontendConfig;

            const scriptElement = document.getElementById("frontend-config");
            if (scriptElement !== null && scriptElement.parentElement !== null) {
                scriptElement.parentElement.removeChild(scriptElement as Node);
            }
        }

        return Utils.loadedConfiguration;
    }

    public static downloadFile(request: XMLHttpRequest): void {
        const blob = request.response;
        const disposition = request.getResponseHeader("Content-Disposition");
        const filename = this.extractFileNameFromDisposition(disposition);
        this.downloadBlobFile(blob, filename);
    }

    public static extractFileNameFromDisposition(disposition: string | null): string {
        let filename = "";
        if (disposition && disposition.indexOf("attachment") !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, "");
            }
        }
        return filename;
    }

    public static downloadBlobFile(data: Blob, fileName?: string): void {
        const link = document.createElement("a");
        const url = window.URL.createObjectURL(data);
        link.href = url;
        if (fileName) {
            link.download = fileName;
        }
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    public static isSameDate(a: Date | null, b: Date | null): boolean {
        return a?.getFullYear() === b?.getFullYear() && a?.getMonth() === b?.getMonth() && a?.getDate() === b?.getDate();
    }

    public static getCookiesEnabled(): boolean {
        const dataAttributeValue = document.getElementById("cookie-allowed")?.getAttribute("data-value")?.toLowerCase();
        return dataAttributeValue ? dataAttributeValue === "true" : false;
    }

    public static getCookie(name: string): string | null {
        name = name + "=";
        const cookieSplited = document.cookie.split(";");
        return (
            cookieSplited
                .map((c) => c.trim())
                .filter((cookie) => cookie.indexOf(name) === 0)
                .map((cookie) => decodeURIComponent(cookie.substring(name.length)))[0] || null
        );
    }

    public static setCookie(name: string, value: string): void {
        name = name + "=";
        const cookieRecord = `${name}${value}`;
        const cookieArray = document.cookie.split(";").map((c) => c.trim());
        const matchingCookie =
            cookieArray.filter((cookie) => cookie.indexOf(name) === 0)[0] ||
            null;
        if (matchingCookie === null) {
            cookieArray.push(cookieRecord);
        } else {
            const indexOfCookie = cookieArray.indexOf(matchingCookie);
            cookieArray[indexOfCookie] = cookieRecord;
        }
        document.cookie = cookieArray.join("; ");
    }
}
